import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import React from 'react';
import { Image, View, type ViewStyle } from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { Card, CardContent } from '@/components/ui/card';
import { PressableScale } from '@/components/ui/pressable-scale';
import { useTerms } from '@/hooks/use-terms';
import { CatalogItem } from '@/types/service-catalog';
import { formatCurrency } from '@/utils/format-currency';

const rowStyle: ViewStyle = {
  borderCurve: 'continuous',
};

/**
 * O que o profissional faz dentro do catálogo do negócio. O item é da
 * organização; o preço é dele — então cada linha leva direto para a própria
 * cópia, e um preço em branco aparece como pendência, não como vazio.
 */
export function MyCatalogServicesCard({ items }: { items: CatalogItem[] }) {
  const terms = useTerms();
  const missingPrice = items.filter((item) => item.mine?.price == null).length;

  return (
    <Card>
      <CardContent className="gap-3 py-4">
        <View className="flex-row items-center gap-2">
          <ThemedText className="flex-1 font-semibold">{`Seus ${terms.servicePlural}`}</ThemedText>
          {items.length ? (
            <View className="rounded-full bg-primary-50 px-2.5 py-1">
              <ThemedText className="font-semibold text-[11px] text-primary-600">
                {items.length}
              </ThemedText>
            </View>
          ) : null}
        </View>

        {items.length ? (
          <>
            <ThemedText className="text-foreground/60 text-sm">
              {`O ${terms.service} é do negócio, o preço é seu. Toque para ajustar o seu.`}
            </ThemedText>

            <View className="gap-2">
              {items.map((item) => {
                const price = item.mine?.price ?? null;
                return (
                  <PressableScale
                    key={item.id}
                    haptic
                    accessibilityRole="button"
                    accessibilityLabel={`Ajustar ${item.name}`}
                    onPress={() =>
                      router.push(`/(tabs)/(provider)/services/${item.mine?.serviceId}` as never)
                    }
                    className="w-full flex-row items-center gap-3 rounded-2xl border border-neutral-900/5 bg-background px-3 py-3"
                    style={rowStyle}
                  >
                    {item.photoUrl ? (
                      <Image
                        source={{ uri: item.photoUrl }}
                        aria-hidden
                        style={{ width: 40, height: 40, borderRadius: 12 }}
                      />
                    ) : (
                      <View className="h-10 w-10 items-center justify-center rounded-xl bg-primary-50 dark:bg-primary">
                        <Ionicons className="text-primary dark:text-white" name="pricetags-outline" size={18} />
                      </View>
                    )}

                    <View className="min-w-0 flex-1 gap-0.5">
                      <ThemedText
                        className="font-semibold text-card-foreground"
                        numberOfLines={1}
                      >
                        {item.name}
                      </ThemedText>
                      {price != null ? (
                        <ThemedText className="font-semibold text-sm text-primary-600">
                          {formatCurrency(price)}
                        </ThemedText>
                      ) : (
                        <ThemedText className="font-medium text-amber-600 text-xs">
                          Defina seu preço
                        </ThemedText>
                      )}
                    </View>

                    <Ionicons className="text-neutral-300" name="chevron-forward" size={16} />
                  </PressableScale>
                );
              })}
            </View>

            {missingPrice ? (
              <View className="flex-row items-center gap-2 rounded-2xl bg-amber-50 px-3 py-2.5">
                <Ionicons className="text-warning-600" name="alert-circle-outline" size={16} />
                <ThemedText className="flex-1 text-amber-700 text-xs">
                  {missingPrice === 1
                    ? `1 ${terms.service} ainda sem preço — o cliente vê "a combinar".`
                    : `${missingPrice} ${terms.servicePlural} ainda sem preço — o cliente vê "a combinar".`}
                </ThemedText>
              </View>
            ) : null}
          </>
        ) : (
          <View className="items-center gap-2 rounded-2xl border border-border border-dashed px-4 py-6">
            <View className="h-12 w-12 items-center justify-center rounded-2xl bg-primary-50 dark:bg-primary">
              <Ionicons className="text-primary dark:text-white" name="pricetags-outline" size={22} />
            </View>
            <ThemedText className="text-center font-semibold">
              {`Nenhum ${terms.service} marcado`}
            </ThemedText>
            <ThemedText className="text-foreground/60 text-center text-sm">
              {`Marque o que você faz no catálogo e o cliente passa a te escolher para esses ${terms.servicePlural}.`}
            </ThemedText>
          </View>
        )}

        <PressableScale
          haptic
          accessibilityRole="button"
          onPress={() => router.push('/(tabs)/(provider)/services')}
          className="w-full flex-row items-center justify-center gap-2 rounded-full bg-primary-50 px-4 py-3"
          style={rowStyle}
        >
          <Ionicons name="add-circle-outline" size={18} color="#FD5001" />
          <ThemedText className="font-semibold text-primary-600 text-sm">
            Escolher o que eu faço
          </ThemedText>
        </PressableScale>
      </CardContent>
    </Card>
  );
}
